/**
 * Custom collection filters — native horizontal scroll + AJAX filtering.
 *
 * Tag buttons use Search & Discovery (filter.p.tag) when that filter is enabled,
 * otherwise native tagged collection URLs (/collections/handle/tag).
 * A button can instead store a storefront filter query and apply those params.
 * Collection links navigate to another collection and are left to the browser.
 */
(function () {
  const ROOT_SELECTOR = "[data-custom-collection-filter]";
  const TRACK_SELECTOR = "[data-custom-collection-filter-track]";
  const TAG_FILTER_PARAM = "filter.p.tag";

  const facetListeners = new Set();
  const clickBoundSections = new Set();
  const bootedRoots = new WeakSet();
  const MOBILE_MQ = window.matchMedia("(max-width: 749px)");
  const DRAG_THRESHOLD = 5;

  function isScrollTrack(root) {
    if (MOBILE_MQ.matches) {
      return true;
    }

    return root?.dataset.enableSlider === "true";
  }

  function bindMouseDrag(trackEl, root) {
    if (!isScrollTrack(root) || trackEl.dataset.ccfMouseDragBound === "true") {
      return;
    }

    trackEl.dataset.ccfMouseDragBound = "true";

    let pointerId = null;
    let dragStartX = 0;
    let lastX = 0;
    let isDragging = false;

    trackEl.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) {
        return;
      }

      if (event.target.closest(".custom-collection-filter__tag-btn")) {
        return;
      }

      if (trackEl.scrollWidth <= trackEl.clientWidth) {
        return;
      }

      pointerId = event.pointerId;
      dragStartX = event.clientX;
      lastX = event.clientX;
      isDragging = false;
      delete trackEl.dataset.ccfDragged;
    });

    trackEl.addEventListener("pointermove", (event) => {
      if (event.pointerId !== pointerId) {
        return;
      }

      if (!isDragging) {
        if (Math.abs(event.clientX - dragStartX) <= DRAG_THRESHOLD) {
          return;
        }

        isDragging = true;
        trackEl.dataset.ccfDragged = "true";
        trackEl.classList.add("is-dragging");
        trackEl.setPointerCapture(event.pointerId);
      }

      const delta = event.clientX - lastX;
      lastX = event.clientX;
      trackEl.scrollBy({ left: -delta });
    });

    const endDrag = (event) => {
      if (event.pointerId !== pointerId) {
        return;
      }

      pointerId = null;
      isDragging = false;
      trackEl.classList.remove("is-dragging");

      if (trackEl.hasPointerCapture(event.pointerId)) {
        trackEl.releasePointerCapture(event.pointerId);
      }

      if (trackEl.dataset.ccfDragged === "true") {
        window.setTimeout(() => {
          delete trackEl.dataset.ccfDragged;
        }, 0);
      }
    };

    trackEl.addEventListener("pointerup", endDrag);
    trackEl.addEventListener("pointercancel", endDrag);
  }

  function getMainCollectionSectionId() {
    const mainCollection = document.querySelector(
      ".shopify-section--main-collection, .shopify-section--main-collection-lulu",
    );
    return mainCollection?.id.replace("shopify-section-", "") || "";
  }

  function getFilterMode(root) {
    return root?.dataset.filterMode === "storefront" ? "storefront" : "path";
  }

  function getActiveTagHandleFromPath(
    collectionUrl,
    pathname = window.location.pathname,
  ) {
    const normalizedPath = pathname.replace(/\/$/, "");
    const basePath = collectionUrl.replace(/\/$/, "");

    if (
      normalizedPath.length <= basePath.length ||
      !normalizedPath.startsWith(`${basePath}/`)
    ) {
      return "";
    }

    return normalizedPath.slice(basePath.length + 1).split("+")[0];
  }

  function parseFilterQuery(raw) {
    const value = (raw || "").trim();

    if (!value) {
      return new URLSearchParams();
    }

    try {
      if (value.includes("://")) {
        return new URL(value).searchParams;
      }
    } catch (error) {
      // The merchant may paste a query string instead of a full URL.
    }

    const withoutHash = value.split("#")[0];
    const query = withoutHash.includes("?")
      ? withoutHash.slice(withoutHash.indexOf("?") + 1)
      : withoutHash.replace(/^\?/, "");

    return new URLSearchParams(query);
  }

  function paramsMatch(searchParams, expected) {
    const keys = [...new Set(expected.keys())];

    if (keys.length === 0) {
      return false;
    }

    return keys.every((key) => {
      const actualValues = searchParams.getAll(key).slice().sort();
      const expectedValues = expected.getAll(key).slice().sort();

      return (
        actualValues.length === expectedValues.length &&
        expectedValues.every((value, index) => value === actualValues[index])
      );
    });
  }

  function collectManagedKeys(root) {
    const keys = new Set([TAG_FILTER_PARAM]);

    root.querySelectorAll("[data-filter-query]").forEach((button) => {
      const params = parseFilterQuery(button.dataset.filterQuery || "");

      for (const key of params.keys()) {
        keys.add(key);
      }
    });

    return keys;
  }

  function getActiveTagFromUrl(root, url = window.location.href) {
    const parsedUrl = new URL(url, window.location.origin);

    if (getFilterMode(root) === "storefront") {
      return parsedUrl.searchParams.get(TAG_FILTER_PARAM) || "";
    }

    return getActiveTagHandleFromPath(
      root.dataset.collectionUrl || "",
      parsedUrl.pathname,
    );
  }

  function getButtonTagValue(button, mode) {
    return mode === "storefront"
      ? button.dataset.tagValue || ""
      : button.dataset.tagHandle || "";
  }

  function findActiveButton(root, url = window.location.href) {
    const parsedUrl = new URL(url, window.location.origin);
    const buttons = [
      ...root.querySelectorAll(".custom-collection-filter__tag-btn"),
    ];
    const queryButton = buttons.find((button) => {
      const query = button.dataset.filterQuery;
      return query && paramsMatch(parsedUrl.searchParams, parseFilterQuery(query));
    });

    if (queryButton) {
      return queryButton;
    }

    const mode = getFilterMode(root);
    const tag = getActiveTagFromUrl(root, parsedUrl.href);
    const tagButton = buttons.find((button) => {
      if (button.dataset.filterQuery || button.hasAttribute("data-filter-reset")) {
        return false;
      }

      const buttonTag = getButtonTagValue(button, mode);
      return buttonTag !== "" && buttonTag === tag;
    });

    if (tagButton) {
      return tagButton;
    }

    if (tag === "") {
      return root.querySelector("[data-filter-reset]");
    }

    return null;
  }

  function urlsEqual(left, right) {
    if (left.pathname.replace(/\/$/, "") !== right.pathname.replace(/\/$/, "")) {
      return false;
    }

    const normalize = (params) =>
      [...params.entries()]
        .filter(([key]) => key !== "section_id")
        .sort(
          (a, b) => a[0].localeCompare(b[0]) || String(a[1]).localeCompare(String(b[1])),
        )
        .map(([key, value]) => `${key}=${value}`)
        .join("&");

    return normalize(left.searchParams) === normalize(right.searchParams);
  }

  function buildFilterUrl(root, button) {
    const collectionUrl = root.dataset.collectionUrl || "";
    const basePath = collectionUrl.replace(/\/$/, "");
    const url = new URL(window.location.href);
    const mode = getFilterMode(root);
    const query = button?.dataset.filterQuery || "";

    url.searchParams.delete("page");

    for (const key of collectManagedKeys(root)) {
      url.searchParams.delete(key);
    }

    if (query) {
      url.pathname = basePath;
      const params = parseFilterQuery(query);

      for (const [key, value] of params.entries()) {
        url.searchParams.append(key, value);
      }
    } else if (button && !button.hasAttribute("data-filter-reset")) {
      const tagValue = getButtonTagValue(button, mode);

      if (mode === "storefront") {
        url.pathname = basePath;

        if (tagValue) {
          url.searchParams.set(TAG_FILTER_PARAM, tagValue);
        }
      } else {
        url.pathname = tagValue ? `${basePath}/${tagValue}` : basePath;
      }
    } else {
      url.pathname = basePath;
    }

    return url;
  }

  function isButtonVisibleInTrack(trackEl, button) {
    const trackRect = trackEl.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();

    return (
      buttonRect.left >= trackRect.left - 2 &&
      buttonRect.right <= trackRect.right + 2
    );
  }

  function scrollActiveButtonIntoView(trackEl, { smooth = false } = {}) {
    if (!trackEl) {
      return;
    }

    const activeButton = trackEl.querySelector(
      ".custom-collection-filter__tag-btn.active",
    );

    if (!activeButton || isButtonVisibleInTrack(trackEl, activeButton)) {
      return;
    }

    activeButton.scrollIntoView({
      inline: "center",
      block: "nearest",
      behavior: smooth ? "smooth" : "instant",
    });
  }

  function syncActiveButtons(root, activeButton) {
    const resolved =
      activeButton === undefined ? findActiveButton(root) : activeButton;

    root
      .querySelectorAll(".custom-collection-filter__tag-btn")
      .forEach((button) => {
        if (button.hasAttribute("data-collection-link")) {
          return;
        }

        const isActive = Boolean(resolved) && button === resolved;

        button.classList.toggle("active", isActive);
        button.setAttribute("aria-selected", isActive ? "true" : "false");
      });
  }

  function applyButtonFilter(root, button) {
    const mainCollectionSectionId = getMainCollectionSectionId();
    const collectionUrl = root.dataset.collectionUrl || "";

    if (!mainCollectionSectionId || !collectionUrl) {
      return;
    }

    const nextUrl = buildFilterUrl(root, button);

    if (urlsEqual(nextUrl, new URL(window.location.href))) {
      syncActiveButtons(root);
      return;
    }

    nextUrl.searchParams.set("section_id", mainCollectionSectionId);

    const nextActive = button || root.querySelector("[data-filter-reset]");
    syncActiveButtons(root, nextActive);

    scrollActiveButtonIntoView(root.querySelector(TRACK_SELECTOR), {
      smooth: true,
    });

    document.dispatchEvent(
      new CustomEvent("facet:update", {
        bubbles: true,
        detail: { url: nextUrl },
      }),
    );
  }

  function bindSectionClicks(sectionEl) {
    const sectionId = sectionEl.id.replace("shopify-section-", "");

    if (clickBoundSections.has(sectionId)) {
      return;
    }

    clickBoundSections.add(sectionId);

    sectionEl.addEventListener("click", (event) => {
      const button = event.target.closest(".custom-collection-filter__tag-btn");

      if (!button || !sectionEl.contains(button)) {
        return;
      }

      const root = button.closest(ROOT_SELECTOR);
      const trackEl = root?.querySelector(TRACK_SELECTOR);

      if (!root) {
        return;
      }

      if (button.hasAttribute("data-collection-link")) {
        if (trackEl?.dataset.ccfDragged === "true") {
          event.preventDefault();
        }

        return;
      }

      const isActive = button.classList.contains("active");
      const showAllButton = root.dataset.showAllButton === "true";

      if (isActive && showAllButton) {
        return;
      }

      if (trackEl?.dataset.ccfDragged === "true") {
        event.preventDefault();
        return;
      }

      const target = isActive && !showAllButton ? null : button;
      applyButtonFilter(root, target);
    });
  }

  function registerFacetListener(sectionId) {
    if (facetListeners.has(sectionId)) {
      return;
    }

    facetListeners.add(sectionId);

    document.addEventListener("facet:update", (event) => {
      const sectionEl = document.getElementById(`shopify-section-${sectionId}`);
      const root = sectionEl?.querySelector(ROOT_SELECTOR);

      if (!root) {
        return;
      }

      const eventUrl = event.detail?.url;

      if (!eventUrl) {
        syncActiveButtons(root);
        return;
      }

      const parsedUrl = new URL(eventUrl, window.location.origin);
      parsedUrl.searchParams.delete("section_id");
      syncActiveButtons(root, findActiveButton(root, parsedUrl.href));
    });
  }

  function bootRoot(root) {
    const sectionId = root.dataset.sectionId;

    if (!sectionId) {
      return;
    }

    const sectionEl = document.getElementById(`shopify-section-${sectionId}`);

    if (!sectionEl) {
      return;
    }

    if (bootedRoots.has(root)) {
      return;
    }

    bootedRoots.add(root);

    bindSectionClicks(sectionEl);
    registerFacetListener(sectionId);
    syncActiveButtons(root);

    const trackEl = root.querySelector(TRACK_SELECTOR);
    bindMouseDrag(trackEl, root);
    scrollActiveButtonIntoView(trackEl);
  }

  function boot(container = document) {
    container.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      bootRoot(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target?.querySelector?.(ROOT_SELECTOR)) {
      boot(event.target);
    }
  });
})();
